import { BookingApiError } from '../services/bookingService';

/**
 * Backend error normalization for the customer SPA.
 *
 * The backend (booking-module / availability-service) sometimes surfaces raw or
 * mixed-language error strings — e.g. an English `ConflictException('Slot is already held by
 * another user')` with a Spanish suffix appended ("... Por favor, intenta seleccionar otro
 * horario."), or an axios default "Request failed with status code 409" that leaked into a
 * response body. Those must never reach the user verbatim: they read as broken, half-English
 * half-Spanish, and ignore the active language.
 *
 * This module maps known error signatures (by `BookingApiError.code`, HTTP status, or a
 * pattern in the message) to clean i18n keys, so the UI always shows a single, fully
 * localized sentence. Unknown messages fall back to a translated generic error rather than
 * the raw backend string.
 */

type Translate = (key: string) => string;

const GENERIC_KEY = 'booking.error_default';

// BookingApiError.code → i18n key
const CODE_TO_KEY: Record<string, string> = {
  SLOT_NOT_FOUND: 'booking.error_slot_taken',
  HOLD_CONFLICT: 'booking.error_slot_taken',
  HOLD_EXPIRED: 'booking.error_hold_expired',
  CAPACITY_EXCEEDED: 'booking.error_capacity',
  PARTY_SIZE_INVALID: 'booking.error_party_size',
  DUPLICATE_BOOKING: 'booking.error_duplicate',
  AUTH_ERROR: 'booking.error_auth',
  MAX_PENDING_BOOKINGS_EXCEEDED: 'booking.error_max_pending',
};

interface Pattern {
  key: string;
  test: (msg: string) => boolean;
}

// Order matters: more specific signatures first.
const MESSAGE_PATTERNS: Pattern[] = [
  {
    // El backend manda ya el código, pero el texto también viaja solo por las rutas que
    // renderizan `uiIntent.data.message` sin error tipado. Sin esta firma, ese texto caía
    // en el 409 genérico de abajo y se convertía en «horario ocupado».
    key: 'booking.error_max_pending',
    test: (m) =>
      /l[íi]mite de \d+ reservas|reservas pendientes|citas pendientes|max(?:imum)? pending bookings/i.test(
        m,
      ),
  },
  {
    key: 'booking.error_slot_taken',
    test: (m) =>
      /already held|slot is (?:already|no longer)|ya (?:est[áa]|no est[áa]) disponible|otro usuario|\b409\b/i.test(
        m,
      ),
  },
  {
    /*
     * AV3: desde oct-2026 el backend rechaza con 400 una reserva que no cabe en el horario del
     * negocio, y el texto que manda es para el dueño («El negocio abre de 09:00 a 19:00 ese día,
     * y la reserva va de 18:30 a 19:30»), no para el cliente. El QA vio en su lugar el
     * «Request failed with status code 400» de axios. Aquí se traduce a una frase que dice qué
     * hacer: elegir otra hora.
     */
    key: 'booking.error_slot_taken',
    test: (m) =>
      /el negocio abre de|no abre ese d[íi]a|fuera del horario|business hours/i.test(m),
  },
  {
    key: 'booking.error_no_availability',
    test: (m) =>
      /no hay horarios|no availability|no slots|sin disponibilidad|no times? available/i.test(m),
  },
  {
    key: 'booking.error_capacity',
    test: (m) => /capacity_exceeded|exceeds? capacity|no hay plazas|sin plazas/i.test(m),
  },
];

/** Try to match a free-text backend message to a clean i18n key. Returns null if unknown. */
function keyForMessage(raw: string, status?: number): string | null {
  const msg = (raw || '').trim();
  // El texto manda sobre el código HTTP. Un 409 significa «conflicto», no «hueco ocupado»:
  // también lo devuelve el tope de citas abiertas, y traducirlo a «este horario acaba de
  // ser ocupado» hacía que el cliente eligiera otra hora y fallara igual, indefinidamente,
  // sin ninguna pista de cuál era el problema real. Un «no» con el motivo equivocado no se
  // discute, se acata.
  for (const p of MESSAGE_PATTERNS) {
    if (p.test(msg)) return p.key;
  }
  // Axios default leaks ("Request failed with status code NNN"): never show verbatim.
  if (/request failed with status code/i.test(msg)) return GENERIC_KEY;
  // 409 sin firma reconocible: error genérico, que es honesto. Antes se afirmaba un motivo
  // concreto que podía ser falso.
  if (status === 409 || status === 400) return GENERIC_KEY;
  return null;
}

/** Resolve any thrown error into a single, fully localized message string. */
export function resolveBookingErrorMessage(err: unknown, t: Translate): string {
  if (err instanceof BookingApiError) {
    if (err.code && CODE_TO_KEY[err.code]) return t(CODE_TO_KEY[err.code]);
    const byMsg = keyForMessage(err.message || '', err.status);
    return t(byMsg ?? GENERIC_KEY);
  }
  if (err instanceof Error) {
    const byMsg = keyForMessage(err.message || '');
    return t(byMsg ?? GENERIC_KEY);
  }
  return t(GENERIC_KEY);
}

/**
 * Normalize a backend-provided display message (e.g. `uiIntent.data.message` on a ShowError).
 * Known error signatures are translated; anything else is returned as-is so legitimate
 * server-composed text (confirmations, etc.) still shows. Empty input → generic error.
 */
export function normalizeBackendErrorMessage(
  raw: string | undefined | null,
  t: Translate,
): string {
  const msg = (raw || '').trim();
  if (!msg) return t(GENERIC_KEY);
  const key = keyForMessage(msg);
  return key ? t(key) : msg;
}
